import { Radio } from "@headlessui/react";
import { useTranslate } from "@lobby/ocb-intl";
import { usePlayerMoneyFormatter } from "../../../../../entities";
import { isNil } from "../../../../../shared";

export function BankAccountField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter bank account number" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "Account number" })}
          {" *"}
        </label>
        <input
          type="text"
          name="payment-account"
          placeholder={$t({ defaultMessage: "Bank account number" })}
          required
          className="input w-full"
        />
      </div>
    </>
  );
}

export function CryptoWalletField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter bank account number" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "Crypto wallet address" })}
          {" *"}
        </label>
        <input
          type="text"
          name="payment-account"
          placeholder={$t({ defaultMessage: "Crypto wallet address" })}
          required
          className="input w-full"
        />
      </div>
    </>
  );
}

export function PhoneNumberField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter phone number" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "Phone number" })}
          {" *"}
        </label>
        <input
          type="tel"
          name="payment-account"
          placeholder={$t({ defaultMessage: "Phone number" })}
          autoComplete="tel"
          required
          pattern="[0-9]{6,100}"
          className="input w-full"
        />
      </div>
    </>
  );
}

export function CardNumberField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter card number" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "Card number" })}
          {" *"}
        </label>
        <input
          type="tel"
          name="payment-account"
          placeholder="xxxx xxxx xxxx xxxx"
          autoComplete="cc-number"
          inputMode="numeric"
          pattern="[0-9\s]{13,19}"
          required
          maxLength={19}
          className="input w-full"
        />
      </div>
    </>
  );
}

export function PixKeyField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter PIX key" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">PIX key *</label>
        <input
          type="text"
          name="payment-account"
          placeholder="PIX key"
          minLength={3}
          maxLength={100}
          required
          className="w-full"
        />
      </div>
    </>
  );
}

interface IPaymentMethodCardProps {
  id: number;
  name: string;
  logoUrl: string | null;
  commission?: string | null;
  limits?: {
    min: number;
    max: number;
  };
}

export function PaymentMethodCard({
  id,
  name,
  logoUrl,
  commission,
  limits,
}: IPaymentMethodCardProps) {
  const { $t } = useTranslate();
  const formatMoney = usePlayerMoneyFormatter();

  return (
    <Radio as="div" value={id} className="payment-method-card">
      <div className="relative grow">
        <img
          className="px-3.5 pt-2.5 pb-5 object-contain absolute size-full"
          src={logoUrl ?? ""}
          alt={name}
        />
        {commission && Number(commission) > 0 && (
          <div
            style={{
              color: "var(--text-secondary-color)",
            }}
            className="absolute right-0 bottom-1 left-0 px-2.5 text-center font-medium text-10/none uppercase"
          >
            {$t(
              { defaultMessage: "commission: {commission}%" },
              { commission }
            )}
          </div>
        )}
      </div>

      {!isNil(limits) && (
        <div
          style={{
            background: "var(--bg-level-0)",
          }}
          className="flex-center whitespace-nowrap px-2.5 py-1.5 text-center text-14/none"
        >
          {formatMoney(limits.min ?? 0, { style: "decimal" })}
          {" - "}
          {formatMoney(limits.max ?? 0)}
        </div>
      )}
    </Radio>
  );
}

export function PIXBillingInformation() {
  const { $t } = useTranslate();

  return (
    <div className="billing-information-block space-y-4">
      <div>
        <label>
          {$t({ defaultMessage: "Phone number" })}
          {" *"}
        </label>
        <input
          type="tel"
          name="billing-pix-mobile"
          placeholder="+79991234567"
          autoComplete="tel"
          minLength={2}
          maxLength={13}
          required
          pattern="[0-9\+]{2,13}"
          title={$t({
            defaultMessage: "Only numbers and + sign are allowed",
          })}
          className="input"
        />
      </div>
      <div>
        <label>
          {$t({ defaultMessage: "Email" })}
          {" *"}
        </label>
        <input
          type="email"
          name="billing-pix-email"
          placeholder="test@gmail.com"
          autoComplete="email"
          pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
          title={$t({
            defaultMessage: "Please enter a valid email address",
          })}
          required
          className="input"
        />
      </div>
      <div>
        <label>
          {$t({ defaultMessage: "Tax number" })}
          {" *"}
        </label>
        <input
          type="text"
          name="billing-pix-tax-number"
          placeholder="123412341234"
          maxLength={20}
          required
          className="input"
        />
      </div>
    </div>
  );
}
